import React, {FC, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import 'moment/locale/fr'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {useAuth} from '../../modules/auth'
import {getClientArrau} from '../../modules/kamelia/client-management/users-list/core/_requests'
import {User as ClientUser} from '../../modules/kamelia/client-management/users-list/core/_models'
import {getBrandsArray} from '../../modules/kamelia/client-view/core/_requests'
import {Brand as ClientBrand} from '../../modules/kamelia/client-view/core/_models'
import {getCAdata, getCAClientdata} from '../../modules/kamelia/dashboard/core/_requests'
import {
  getCollaboratorsArray,
  getUser,
} from '../../modules/kamelia/collaborator-management/users-list/core/_requests'
import {User as CollaboratorUser} from '../../modules/kamelia/collaborator-management/users-list/core/_models'
import {getBadges} from '../../modules/kamelia/collaborator-view/core/_requests'
import {Badge} from '../../modules/kamelia/collaborator-view/core/_models'
import {getContracts} from '../../modules/kamelia/contract/core/_requests'
import {getLocationsArray} from '../../modules/kamelia/location-management/location-list/core/_requests'
import {Location} from '../../modules/kamelia/location-management/location-list/core/_models'
import {getPlannings, getPlanningsAvailable} from '../../modules/kamelia/planning/core/_requests'
import {Planning} from '../../modules/kamelia/planning/core/_models'
import {getQuotations} from '../../modules/kamelia/quotation-management/quotation-list/core/_requests'
import {Quotation} from '../../modules/kamelia/quotation-management/quotation-list/core/_models'
import {getVoicesArray} from '../../modules/kamelia/voice-management/voice-list/core/_requests'
import {Voice} from '../../modules/kamelia/voice-management/voice-list/core/_models'
import './dashboard.css'

type RevenuePoint = {
  id?: string
  amount?: number
}

type TopClient = {
  name: string
  amount: number
  percent: number
}

type WorkItem = {
  label: string
  description: string
  value: number
  tone: 'warning' | 'danger' | 'primary'
  target: string
}

type RenewalDocumentType = 'badge' | 'identity'

type RenewalRow = {
  id: string
  collaboratorId?: string
  initials: string
  fullName: string
  role: string
  personMeta: string
  documentType: RenewalDocumentType
  documentLabel: string
  reference: string
  client: string
  location: string
  hasAssignment: boolean
  dueDate: moment.Moment
  daysLeft: number
}

type DashboardData = {
  invoices: Voice[]
  quotations: Quotation[]
  plannings: Planning[]
  previousPlannings: Planning[]
  collaborators: CollaboratorUser[]
  badges: Badge[]
  clients: ClientUser[]
  brands: ClientBrand[]
  locations: Location[]
  planningState: any
  contracts: any[]
  contractsCount: number
  turnover: RevenuePoint[]
  turnoverByClient: RevenuePoint[]
}

type MetricCardProps = {
  accent: 'primary' | 'warning' | 'purple' | 'success'
  icon: string
  label: string
  value: string
  suffix?: string
  detail: React.ReactNode
  target: string
}

const safeNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/'/g, '').replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatMoney = (value: number, compact = false) => {
  const rounded = Math.round(value)
  if (compact && Math.abs(rounded) >= 1000) {
    return `CHF ${Math.round(rounded / 1000)}k`
  }
  return `CHF ${new Intl.NumberFormat('fr-CH').format(rounded)}`
}

const formatAxisMoney = (value: number) => {
  if (value >= 1000) {
    return `CHF ${new Intl.NumberFormat('fr-CH', {maximumFractionDigits: 1}).format(value / 1000)}k`
  }
  return formatMoney(value)
}

const normalizeKey = (value?: string) => moment(value || '').format('YYYY-MM')

const amountOf = (item: any) =>
  safeNumber(item?.net_amount ?? item?.amount ?? item?.total_amount ?? item?.hour_amount)

const isInMonth = (value: any, month: moment.Moment) => {
  if (!value) {
    return false
  }
  const date = moment(value)
  return date.isValid() && date.isSame(month, 'month')
}

const isToInvoice = (quotation: Quotation) => {
  return quotation.status === 'TOBILL'
}

const hasAssignedCollaborator = (planning: Planning) => {
  const collaborator = planning.collaborator as any
  if (!collaborator) {
    return false
  }
  if (typeof collaborator === 'string') {
    return collaborator.trim().length > 0
  }
  return Boolean(collaborator._id || collaborator.id)
}

const getPlanningHours = (planning: Planning) => {
  if (!planning.from || !planning.to) {
    return 0
  }

  const from = moment(planning.from)
  const to = moment(planning.to)
  if (from.isValid() && to.isValid()) {
    return Math.max(to.diff(from, 'minutes') / 60, 0)
  }

  const [fromHours, fromMinutes] = `${planning.from}`.split(':').map(Number)
  const [toHours, toMinutes] = `${planning.to}`.split(':').map(Number)
  if (
    Number.isFinite(fromHours) &&
    Number.isFinite(fromMinutes) &&
    Number.isFinite(toHours) &&
    Number.isFinite(toMinutes)
  ) {
    return Math.max((toHours * 60 + toMinutes - (fromHours * 60 + fromMinutes)) / 60, 0)
  }

  return 0
}

const getFirstDateValue = (source: any, keys: string[]) =>
  keys.map((key) => source?.[key]).find((value) => Boolean(value))

const isExpiringWithin = (value: unknown, reference: moment.Moment, days = 30) => {
  if (!value) {
    return false
  }
  const date = moment(value as any)
  const remainingDays = date.diff(reference, 'days')
  return date.isValid() && remainingDays >= 0 && remainingDays <= days
}

const getLocationLabel = (location: unknown) => {
  if (typeof location === 'string' && location.trim()) {
    return location
  }

  if (location && typeof location === 'object') {
    const locationData = location as {
      name?: string
      label?: string
      title?: string
      city?: string
      address?: string
    }
    return (
      locationData.name ||
      locationData.label ||
      locationData.title ||
      locationData.city ||
      locationData.address ||
      ''
    )
  }

  return ''
}

const getPlanningLocationLabel = (planning: Planning) =>
  getLocationLabel(planning.location) || planning.brand?.name || 'Lieu non renseigné'

const getPlanningClientLabel = (planning: Planning) =>
  planning.brand?.name || 'Client non renseigné'

const normalizeLabel = (value?: string) =>
  (value || '')
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const getCollaboratorDisplayName = (collaborator: CollaboratorUser) => {
  const name = [collaborator.firstname, collaborator.lastname].filter(Boolean).join(' ').trim()
  return name || collaborator.email || 'Promoteur sans nom'
}

const getCollaboratorInitials = (collaborator: CollaboratorUser) => {
  const initials = [collaborator.firstname, collaborator.lastname]
    .map((value) => value?.trim()?.[0])
    .filter(Boolean)
    .join('')
  return (initials || getCollaboratorDisplayName(collaborator).slice(0, 2)).toUpperCase()
}

const getRenewalTone = (daysLeft: number) => {
  if (daysLeft <= 7) {
    return 'danger'
  }
  if (daysLeft <= 15) {
    return 'warning'
  }
  return 'success'
}

const getRenewalDelayLabel = (daysLeft: number) => {
  if (daysLeft === 0) {
    return "Aujourd'hui"
  }
  return `Dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`
}

const getPlanningCollaboratorId = (planning: Planning) => {
  const collaborator = planning.collaborator as any
  if (!collaborator) {
    return ''
  }
  return typeof collaborator === 'string' ? collaborator : collaborator._id || collaborator.id || ''
}

const getBadgeCollaboratorId = (badge: any) => {
  const collaborator = badge?.collaborator
  if (!collaborator) {
    return ''
  }
  return typeof collaborator === 'string' ? collaborator : collaborator._id || collaborator.id || ''
}

const getBadgeExpireDate = (badge: any) =>
  getFirstDateValue(badge, ['expire_at', 'expireAt', 'expires_at', 'expiresAt', 'expiration_date'])

const getBadgeReference = (badge: any, index: number) =>
  badge?.number || badge?.reference || badge?.label || `BADGE-${String(index + 1).padStart(4, '0')}`

const getCollaboratorId = (collaborator: any) => String(collaborator?._id || collaborator?.id || '')

const shouldFetchCollaboratorDetailsForRenewals = (collaborator: any, today: moment.Moment) =>
  isExpiringWithin(getFirstDateValue(collaborator, ['pid_expire_at', 'pidExpireAt']), today) ||
  isExpiringWithin(getFirstDateValue(collaborator, ['badge_expire_at', 'badgeExpireAt']), today)

const enrichCollaboratorsWithRenewalDetails = async (collaborators: CollaboratorUser[]) => {
  const today = moment().startOf('day')
  const candidates = collaborators.filter((collaborator: any) =>
    shouldFetchCollaboratorDetailsForRenewals(collaborator, today)
  )

  if (candidates.length === 0) {
    return collaborators
  }

  const details = await Promise.allSettled(
    candidates.map((collaborator: any) => getUser(getCollaboratorId(collaborator)))
  )
  const detailsById = details.reduce<Record<string, CollaboratorUser>>((acc, result) => {
    if (result.status === 'fulfilled' && result.value) {
      acc[getCollaboratorId(result.value)] = result.value
    }
    return acc
  }, {})

  return collaborators.map((collaborator: any) => ({
    ...collaborator,
    ...(detailsById[getCollaboratorId(collaborator)] || {}),
  }))
}

const getCollaboratorBadges = (collaborator: any, badges: Badge[]) => {
  const collaboratorId = getCollaboratorId(collaborator)
  const embeddedBadges = Array.isArray(collaborator.badges) ? collaborator.badges : []
  const linkedBadges = collaboratorId
    ? badges.filter((badge: any) => String(getBadgeCollaboratorId(badge)) === collaboratorId)
    : []
  const seen = new Set<string>()

  return [...embeddedBadges, ...linkedBadges].filter((badge: any) => {
    const key = String(
      badge?._id || badge?.id || `${getBadgeReference(badge, 0)}-${getBadgeExpireDate(badge)}`
    )
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

const getRenewalRows = (
  collaborators: CollaboratorUser[],
  plannings: Planning[],
  badges: Badge[]
) => {
  const today = moment().startOf('day')
  const nextPlanningByCollaborator = plannings.reduce<Record<string, Planning>>((acc, planning) => {
    const collaboratorId = String(getPlanningCollaboratorId(planning))
    if (!collaboratorId) {
      return acc
    }
    const existing = acc[collaboratorId]
    const planningDate = moment(planning.from)
    const existingDate = existing ? moment(existing.from) : undefined
    if (
      !existing ||
      (planningDate.isValid() &&
        (!existingDate?.isValid() || planningDate.isBefore(existingDate, 'day')))
    ) {
      acc[collaboratorId] = planning
    }
    return acc
  }, {})

  return collaborators
    .flatMap((collaborator: any) => {
      const collaboratorId = String(collaborator._id || collaborator.id || '')
      const planning = collaboratorId ? nextPlanningByCollaborator[collaboratorId] : undefined
      const fullName = getCollaboratorDisplayName(collaborator)
      const base = {
        collaboratorId,
        initials: getCollaboratorInitials(collaborator),
        fullName,
        role: collaborator.job || 'Promoteur',
        personMeta: collaborator.email || 'Collaborateur',
        client: planning?.brand?.name || 'Non affecté',
        location: planning ? getPlanningLocationLabel(planning) : 'Aucune affectation à venir',
        hasAssignment: Boolean(planning),
      }
      const buildRow = (
        value: unknown,
        documentType: RenewalDocumentType,
        documentLabel: string,
        reference: string,
        index = 0
      ): RenewalRow | undefined => {
        if (!value || !isExpiringWithin(value, today)) {
          return undefined
        }
        const dueDate = moment(value as any)
        const daysLeft = dueDate.diff(today, 'days')
        return {
          ...base,
          id: `${collaboratorId || fullName}-${documentType}-${index}-${dueDate.format(
            'YYYY-MM-DD'
          )}`,
          documentType,
          documentLabel,
          reference,
          dueDate,
          daysLeft,
        }
      }

      const directBadgeDate = getFirstDateValue(collaborator, ['badge_expire_at', 'badgeExpireAt'])
      const collaboratorBadges = getCollaboratorBadges(collaborator, badges)
      return [
        buildRow(
          directBadgeDate,
          'badge',
          'Badge promoteur',
          collaborator.badge_number || collaborator.badgeNumber || 'BADGE'
        ),
        ...collaboratorBadges.map((badge: any, index: number) =>
          buildRow(
            getBadgeExpireDate(badge),
            'badge',
            'Badge promoteur',
            getBadgeReference(badge, index),
            index + 1
          )
        ),
        buildRow(
          getFirstDateValue(collaborator, ['pid_expire_at', 'pidExpireAt']),
          'identity',
          "Pièce d'identité",
          collaborator.pid_number || collaborator.pidNumber || 'CI'
        ),
      ].filter(Boolean) as RenewalRow[]
    })
    .sort((a, b) => a.daysLeft - b.daysLeft || a.fullName.localeCompare(b.fullName))
}

const MetricCard: FC<MetricCardProps> = ({accent, icon, label, value, suffix, detail, target}) => {
  const navigate = useNavigate()

  return (
    <button
      aria-label={label}
      className={`dashboard-metric dashboard-metric-${accent}`}
      type='button'
      onClick={() => navigate(target)}
    >
      <div className='d-flex justify-content-between align-items-start'>
        <div className='dashboard-metric-label'>{label}</div>
        <span className='dashboard-icon'>
          <i className={`bi ${icon}`} />
        </span>
      </div>
      <div className='dashboard-metric-value'>
        {value}
        {suffix && <span>{suffix}</span>}
      </div>
      <div className='dashboard-metric-detail'>{detail}</div>
    </button>
  )
}

const WorkQueue: FC<{items: WorkItem[]}> = ({items}) => {
  const navigate = useNavigate()
  const urgent = items
    .filter((item) => item.tone === 'danger')
    .reduce((sum, item) => sum + item.value, 0)
  const total = items.reduce((sum, item) => sum + item.value, 0)

  return (
    <div className='dashboard-card h-100'>
      <div className='dashboard-card-header'>
        <div className='d-flex gap-4 align-items-center'>
          <span className='dashboard-square-icon dashboard-square-icon-danger'>
            <i className='bi bi-list-task' />
          </span>
          <div>
            <h3>À traiter</h3>
            <p>Actions prioritaires</p>
          </div>
        </div>
        <div className='dashboard-card-meta'>
          <span className='dashboard-card-meta-urgent'>{urgent} urgentes</span>
          <span> · {total} au total</span>
        </div>
      </div>

      <div className='dashboard-work-list'>
        {items.map((item) => (
          <button
            className='dashboard-work-item'
            key={item.label}
            type='button'
            onClick={() => navigate(item.target)}
          >
            <span className={`dashboard-dot dashboard-dot-${item.tone}`} />
            <div className='flex-grow-1'>
              <div className='dashboard-work-title'>{item.label}</div>
              <div className='dashboard-work-desc'>{item.description}</div>
            </div>
            <div className={`dashboard-work-value dashboard-work-value-${item.tone}`}>
              {item.value}
            </div>
            <i className='bi bi-chevron-right text-muted' />
          </button>
        ))}
      </div>

      <div className='dashboard-source'>Source : quotation, invoice, planning · temps réel</div>
    </div>
  )
}

const RevenueChart: FC<{
  data: Array<{label: string; actual: number}>
  actualTotal: number
  invoiceCount: number
}> = ({data, actualTotal, invoiceCount}) => {
  const rawMaxValue = Math.max(...data.map((item) => item.actual), 1)
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMaxValue)))
  const scaleStep = [1, 2, 5, 10].find((step) => step * magnitude >= rawMaxValue) || 10
  const scaleMax = scaleStep * magnitude
  const yAxisValues = [scaleMax, scaleMax * 0.75, scaleMax * 0.5, scaleMax * 0.25, 0]
  const activeMonths = data.filter((item) => item.actual > 0)
  const bestMonth = activeMonths.reduce((best, item) => (item.actual > best.actual ? item : best), {
    label: '-',
    actual: 0,
  })
  const averageActiveMonth =
    activeMonths.length > 0
      ? Math.round(activeMonths.reduce((sum, item) => sum + item.actual, 0) / activeMonths.length)
      : 0

  return (
    <div className='dashboard-card h-100'>
      <div className='dashboard-card-header'>
        <div className='d-flex gap-4 align-items-center'>
          <span className='dashboard-square-icon dashboard-square-icon-primary'>
            <i className='bi bi-graph-up' />
          </span>
          <div>
            <h3>CA facturé par mois</h3>
            <p>6 derniers mois · CHF HT</p>
          </div>
        </div>
        <div className='dashboard-legend'>
          <span>
            <i className='dashboard-legend-actual' /> Réalisé
          </span>
          <span>
            <i className='dashboard-legend-placeholder' /> Prévision
          </span>
        </div>
      </div>

      <div className='dashboard-chart'>
        <div className='dashboard-y-axis'>
          <span className='dashboard-axis-title'>CHF HT</span>
          {yAxisValues.map((value) => (
            <span key={value}>{value === 0 ? '0' : formatAxisMoney(value)}</span>
          ))}
        </div>
        <div className='dashboard-bars'>
          {data.map((item) => (
            <div className='dashboard-bar-group' key={item.label}>
              <div className='dashboard-bar-pair'>
                <div
                  aria-label={`${item.label} réalisé : ${formatMoney(item.actual)}`}
                  className='dashboard-bar dashboard-bar-actual'
                  tabIndex={item.actual ? 0 : -1}
                  style={{
                    height: `${Math.max((item.actual / scaleMax) * 100, item.actual ? 5 : 0)}%`,
                  }}
                >
                  {item.actual > 0 && (
                    <>
                      <span className='dashboard-bar-value'>{formatMoney(item.actual)}</span>
                      <span className='dashboard-bar-tooltip'>{formatMoney(item.actual)}</span>
                    </>
                  )}
                </div>
                <div
                  aria-label={`${item.label} prévision : ${formatMoney(0)}`}
                  className='dashboard-bar dashboard-bar-placeholder'
                  tabIndex={0}
                >
                  <span className='dashboard-bar-tooltip'>{formatMoney(0)}</span>
                </div>
              </div>
              <div className='dashboard-bar-caption'>
                <span className='dashboard-bar-label'>{item.label}</span>
                <span
                  className={
                    item.actual > 0 ? 'dashboard-bar-amount' : 'dashboard-bar-amount-muted'
                  }
                >
                  {formatMoney(item.actual)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='dashboard-chart-note'>
        <span>
          Total 6 mois : <strong>{formatMoney(actualTotal)}</strong>
        </span>
        <span>
          Mois avec CA : <strong>{activeMonths.length}/6</strong>
        </span>
        {bestMonth.actual > 0 && (
          <span>
            Meilleur mois :{' '}
            <strong>
              {bestMonth.label} · {formatMoney(bestMonth.actual)}
            </strong>
          </span>
        )}
        {averageActiveMonth > 0 && (
          <span>
            Moyenne active : <strong>{formatMoney(averageActiveMonth)}</strong>
          </span>
        )}
      </div>

      <div className='dashboard-chart-summary'>
        <div>
          <span>Factures du mois</span>
          <strong>{invoiceCount}</strong>
        </div>
        <div>
          <span>Mois avec CA</span>
          <strong>{activeMonths.length}/6</strong>
        </div>
        <div>
          <span>CA cumulé</span>
          <strong>{formatMoney(actualTotal)}</strong>
        </div>
        <div>
          <span>CA moyen actif</span>
          <strong>{formatMoney(averageActiveMonth)}</strong>
        </div>
      </div>

      <div className='dashboard-source'>Source : report/turnover, invoice · 6 derniers mois</div>
    </div>
  )
}

const PlanningHealth: FC<{
  plannings: Planning[]
  previousPlannings: Planning[]
  collaborators: CollaboratorUser[]
  badges: Badge[]
  clients: ClientUser[]
  brands: ClientBrand[]
  locations: Location[]
}> = ({plannings, previousPlannings, collaborators, badges, clients, brands, locations}) => {
  const navigate = useNavigate()
  const currentMonth = moment().locale('fr').startOf('month')
  const today = moment().startOf('day')
  const clientNameById = clients.reduce<Record<string, string>>((acc, client) => {
    if (client._id && client.name) {
      acc[String(client._id)] = client.name
    }
    if (client.id && client.name) {
      acc[String(client.id)] = client.name
    }
    return acc
  }, {})
  const clientNameByLabel = clients.reduce<Record<string, string>>((acc, client) => {
    const key = normalizeLabel(client.name)
    if (key && client.name) {
      acc[key] = client.name
    }
    return acc
  }, {})
  const clientIdByBrandKey = brands.reduce<Record<string, string>>((acc, brand) => {
    const clientId = brand.client ? String(brand.client) : ''
    if (brand._id && brand.client) {
      acc[String(brand._id)] = clientId
    }
    if (brand.id && brand.client) {
      acc[String(brand.id)] = clientId
    }
    const brandNameKey = normalizeLabel(brand.name)
    if (brandNameKey && brand.client) {
      acc[brandNameKey] = clientId
    }
    return acc
  }, {})
  const getPlanningClientName = (planning: Planning) => {
    const brand = planning.brand as any
    const brandId = brand?._id || brand?.id
    const brandNameKey = normalizeLabel(brand?.name)
    const clientId =
      (brandId && clientIdByBrandKey[String(brandId)]) || clientIdByBrandKey[brandNameKey]
    return (
      (clientId && clientNameById[clientId]) ||
      clientNameByLabel[brandNameKey] ||
      getPlanningClientLabel(planning)
    )
  }
  const locationNameByKey = locations.reduce<Record<string, string>>((acc, location) => {
    const keys = [location._id, location.id, location.slug, normalizeLabel(location.name)].filter(
      Boolean
    )
    keys.forEach((key) => {
      if (location.name) {
        acc[String(key)] = location.name
      }
    })
    return acc
  }, {})
  const getDashboardPlanningLocationLabel = (planning: Planning) => {
    const location = planning.location as any
    if (typeof location === 'string') {
      return locationNameByKey[location] || locationNameByKey[normalizeLabel(location)] || location
    }

    const locationId = location?._id || location?.id
    const locationSlug = location?.slug
    const locationNameKey = normalizeLabel(location?.name || location?.label || location?.title)
    return (
      (locationId && locationNameByKey[String(locationId)]) ||
      (locationSlug && locationNameByKey[String(locationSlug)]) ||
      locationNameByKey[locationNameKey] ||
      getPlanningLocationLabel(planning)
    )
  }
  const collaboratorNameById = collaborators.reduce<Record<string, string>>((acc, collaborator) => {
    const name = [collaborator.firstname, collaborator.lastname].filter(Boolean).join(' ').trim()
    if (!name) {
      return acc
    }
    if (collaborator._id) {
      acc[String(collaborator._id)] = name
    }
    if (collaborator.id) {
      acc[String(collaborator.id)] = name
    }
    return acc
  }, {})
  const getDashboardPlanningCollaboratorLabel = (planning: Planning) => {
    const collaborator = planning.collaborator as any
    if (!collaborator) {
      return ''
    }
    if (typeof collaborator === 'string') {
      return collaboratorNameById[collaborator] || collaborator
    }
    const collaboratorId = collaborator._id || collaborator.id
    const collaboratorName = [collaborator.firstname, collaborator.lastname]
      .filter(Boolean)
      .join(' ')
      .trim()
    return (collaboratorId && collaboratorNameById[String(collaboratorId)]) || collaboratorName
  }
  const badgeExpirations = collaborators.filter((collaborator: any) => {
    const directBadgeDate = getFirstDateValue(collaborator, ['badge_expire_at', 'badgeExpireAt'])
    const collaboratorBadges = getCollaboratorBadges(collaborator, badges)
    return (
      isExpiringWithin(directBadgeDate, today) ||
      collaboratorBadges.some((badge: any) => isExpiringWithin(getBadgeExpireDate(badge), today))
    )
  })
  const residencePermitExpirations = collaborators.filter((collaborator: any) =>
    isExpiringWithin(
      getFirstDateValue(collaborator, [
        'residence_permit_expire_at',
        'residencePermitExpireAt',
        'stay_permit_expire_at',
        'work_permit_expire_at',
        'permit_expire_at',
        'tds_expire_at',
      ]),
      today
    )
  )
  const identityExpirations = collaborators.filter((collaborator: any) =>
    isExpiringWithin(getFirstDateValue(collaborator, ['pid_expire_at', 'pidExpireAt']), today)
  )
  const formatExpirationDetail = (count: number) =>
    count > 0 ? `${count} document${count > 1 ? 's' : ''} à vérifier` : 'Aucune alerte'
  const alertCards = [
    {
      icon: 'bi-credit-card-2-front',
      label: 'Badges promoteurs',
      value: badgeExpirations.length,
      detail: formatExpirationDetail(badgeExpirations.length),
      tone: 'warning',
    },
    {
      icon: 'bi-building',
      label: 'Titres de séjour',
      value: residencePermitExpirations.length,
      detail: formatExpirationDetail(residencePermitExpirations.length),
      tone: 'danger',
    },
    {
      icon: 'bi-person-vcard',
      label: "Pièces d'identité",
      value: identityExpirations.length,
      detail: formatExpirationDetail(identityExpirations.length),
      tone: 'primary',
    },
  ]
  const allClientNames = Array.from(
    new Set([
      ...clients.map((client) => client.name).filter(Boolean),
      ...previousPlannings.map(getPlanningClientName),
      ...plannings.map(getPlanningClientName),
    ])
  ) as string[]
  const clientPlanningRows = Object.values(
    [...previousPlannings, ...plannings].reduce<
      Record<
        string,
        {
          client: string
          shifts: number
          previousShifts: number
          hours: number
          assigned: number
        }
      >
    >(
      (acc, planning, index) => {
        const client = getPlanningClientName(planning)
        const isCurrentMonthPlanning = index >= previousPlannings.length
        acc[client] = acc[client] || {client, shifts: 0, previousShifts: 0, hours: 0, assigned: 0}

        if (isCurrentMonthPlanning) {
          acc[client].shifts += 1
          acc[client].hours += getPlanningHours(planning)
          if (hasAssignedCollaborator(planning)) {
            acc[client].assigned += 1
          }
        } else {
          acc[client].previousShifts += 1
        }

        return acc
      },
      allClientNames.reduce<
        Record<
          string,
          {client: string; shifts: number; previousShifts: number; hours: number; assigned: number}
        >
      >((acc, client) => {
        acc[client] = {client, shifts: 0, previousShifts: 0, hours: 0, assigned: 0}
        return acc
      }, {})
    )
  )
  const clientInsightRows = clientPlanningRows
    .map((row) => {
      const delta = row.shifts - row.previousShifts
      return {
        ...row,
        delta,
      }
    })
    .sort(
      (a, b) =>
        Math.abs(b.delta) - Math.abs(a.delta) ||
        b.shifts + b.previousShifts - (a.shifts + a.previousShifts) ||
        a.client.localeCompare(b.client)
    )
  const locationPlanningRows = Object.values(
    [...previousPlannings, ...plannings].reduce<
      Record<string, {label: string; shifts: number; previousShifts: number}>
    >(
      (acc, planning, index) => {
        const label = getDashboardPlanningLocationLabel(planning)
        const isCurrentMonthPlanning = index >= previousPlannings.length
        acc[label] = acc[label] || {label, shifts: 0, previousShifts: 0}

        if (isCurrentMonthPlanning) {
          acc[label].shifts += 1
        } else {
          acc[label].previousShifts += 1
        }

        return acc
      },
      locations.reduce<Record<string, {label: string; shifts: number; previousShifts: number}>>(
        (acc, location) => {
          if (location.name) {
            acc[location.name] = {label: location.name, shifts: 0, previousShifts: 0}
          }
          return acc
        },
        {}
      )
    )
  )
  const locationInsightRows = locationPlanningRows
    .map((row) => ({
      ...row,
      delta: row.shifts - row.previousShifts,
    }))
    .sort(
      (a, b) =>
        Math.abs(b.delta) - Math.abs(a.delta) ||
        b.shifts + b.previousShifts - (a.shifts + a.previousShifts) ||
        a.label.localeCompare(b.label)
    )
  const collaboratorPlanningRows = Object.values(
    [...previousPlannings, ...plannings].reduce<
      Record<string, {label: string; shifts: number; previousShifts: number}>
    >(
      (acc, planning, index) => {
        const label = getDashboardPlanningCollaboratorLabel(planning)
        if (!label) {
          return acc
        }
        const isCurrentMonthPlanning = index >= previousPlannings.length
        acc[label] = acc[label] || {label, shifts: 0, previousShifts: 0}

        if (isCurrentMonthPlanning) {
          acc[label].shifts += 1
        } else {
          acc[label].previousShifts += 1
        }

        return acc
      },
      collaborators.reduce<Record<string, {label: string; shifts: number; previousShifts: number}>>(
        (acc, collaborator) => {
          const label = [collaborator.firstname, collaborator.lastname]
            .filter(Boolean)
            .join(' ')
            .trim()
          if (label) {
            acc[label] = {label, shifts: 0, previousShifts: 0}
          }
          return acc
        },
        {}
      )
    )
  )
  const collaboratorInsightRows = collaboratorPlanningRows
    .map((row) => ({
      ...row,
      delta: row.shifts - row.previousShifts,
    }))
    .sort(
      (a, b) =>
        Math.abs(b.delta) - Math.abs(a.delta) ||
        b.shifts + b.previousShifts - (a.shifts + a.previousShifts) ||
        a.label.localeCompare(b.label)
    )
  const renderShiftAccordion = (
    title: string,
    rows: Array<{label: string; shifts: number; previousShifts: number; delta: number}>
  ) => (
    <details className='dashboard-shift-accordion'>
      <summary>
        <span>{title}</span>
        <strong>{rows.length}</strong>
        <i className='bi bi-chevron-down' />
      </summary>
      <div className='dashboard-client-shift-table'>
        {rows.map((row) => {
          const isUp = row.delta > 0
          const isDown = row.delta < 0
          const deltaLabel =
            row.delta === 0
              ? '0 shift'
              : `${isUp ? '+' : ''}${row.delta} shift${Math.abs(row.delta) > 1 ? 's' : ''}`

          return (
            <div className='dashboard-client-shift-table-row' key={`${title}-${row.label}`}>
              <strong>{row.label}</strong>
              <span>{row.shifts} ce mois-ci</span>
              <span>{row.previousShifts} mois dernier</span>
              <b className={isUp ? 'text-success' : isDown ? 'text-danger' : ''}>{deltaLabel}</b>
            </div>
          )
        })}
      </div>
    </details>
  )
  return (
    <div className='dashboard-card dashboard-planning-shell h-100'>
      <div className='dashboard-card-header'>
        <div className='d-flex gap-4 align-items-center'>
          <span className='dashboard-square-icon dashboard-square-icon-purple'>
            <i className='bi bi-clipboard2-check' />
          </span>
          <div>
            <h3>Couverture planning</h3>
            <p>Actions à réaliser · {currentMonth.format('MMMM YYYY')}</p>
          </div>
        </div>
        <button
          className='dashboard-card-link'
          type='button'
          onClick={() => navigate('/admin/planning/overview')}
        >
          Ouvrir
          <i className='bi bi-arrow-right' />
        </button>
      </div>

      <div className='dashboard-section-label dashboard-documents-label'>
        Documents à renouveler · 30 prochains jours
      </div>
      <div className='dashboard-attention-grid'>
        {alertCards.map((alert) => (
          <div
            className={`dashboard-attention-card dashboard-attention-card-${alert.tone}`}
            key={alert.label}
          >
            <div className='dashboard-attention-card-top'>
              <span className='dashboard-attention-icon'>
                <i className={`bi ${alert.icon}`} />
              </span>
              <strong>{alert.value}</strong>
            </div>
            <div className='dashboard-attention-content'>
              <span>{alert.label}</span>
              <p>{alert.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <div className='dashboard-client-shift-panel'>
        <div className='dashboard-client-shift-header'>
          <div>
            <div className='dashboard-section-label'>Comparatif mensuel des shifts</div>
          </div>
        </div>
        {clientInsightRows.length > 0 ? (
          <>
            {renderShiftAccordion(
              'Clients',
              clientInsightRows.map((row) => ({...row, label: row.client}))
            )}
            {renderShiftAccordion('Lieux', locationInsightRows)}
            {renderShiftAccordion('Promoteurs', collaboratorInsightRows)}
          </>
        ) : (
          <div className='dashboard-empty'>Aucun shift sur cette période.</div>
        )}
      </div>
    </div>
  )
}

const TopClients: FC<{clients: TopClient[]}> = ({clients}) => (
  <div className='dashboard-card h-100'>
    <div className='dashboard-card-header'>
      <div className='d-flex gap-4 align-items-center'>
        <span className='dashboard-square-icon dashboard-square-icon-success'>
          <i className='bi bi-people' />
        </span>
        <div>
          <h3>Top 5 clients</h3>
          <p>Par CA facturé · {moment().format('MMMM YYYY')}</p>
        </div>
      </div>
    </div>

    <div className='dashboard-client-list'>
      {clients.map((client, index) => (
        <div className='dashboard-client-row' key={client.name}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <div>
            <div className='d-flex justify-content-between gap-4'>
              <strong>{client.name}</strong>
              <strong>{formatMoney(client.amount)}</strong>
              <span>{client.percent}%</span>
            </div>
            <div className='dashboard-client-progress'>
              <div style={{width: `${client.percent}%`}} />
            </div>
          </div>
        </div>
      ))}
      {clients.length === 0 && (
        <div className='dashboard-empty'>Aucun client facturé ce mois-ci.</div>
      )}
    </div>

    <div className='dashboard-client-note'>
      Ces {clients.length || 0} clients représentent{' '}
      <strong>{clients.reduce((sum, client) => sum + client.percent, 0)}%</strong> du CA facturé du
      mois.
    </div>
    <div className='dashboard-source'>
      Source : report/turnover · {moment().format('MMMM YYYY')}
    </div>
  </div>
)

const DocumentRenewalsTable: FC<{
  collaborators: CollaboratorUser[]
  plannings: Planning[]
  badges: Badge[]
}> = ({collaborators, plannings, badges}) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<'all' | RenewalDocumentType>('all')
  const [urgentFirst, setUrgentFirst] = useState(true)
  const rows = useMemo(
    () => getRenewalRows(collaborators, plannings, badges),
    [collaborators, plannings, badges]
  )
  const normalizedSearch = normalizeLabel(search)
  const filteredRows = rows
    .filter((row) => activeTab === 'all' || row.documentType === activeTab)
    .filter((row) => {
      if (!normalizedSearch) {
        return true
      }
      return [row.fullName, row.role, row.documentLabel, row.reference, row.client, row.location]
        .map(normalizeLabel)
        .some((value) => value.includes(normalizedSearch))
    })
    .sort((a, b) =>
      urgentFirst
        ? a.daysLeft - b.daysLeft || a.fullName.localeCompare(b.fullName)
        : a.fullName.localeCompare(b.fullName) || a.daysLeft - b.daysLeft
    )
  const badgeCount = rows.filter((row) => row.documentType === 'badge').length
  const identityCount = rows.filter((row) => row.documentType === 'identity').length
  const tabs = [
    {id: 'all' as const, label: 'Tous', count: rows.length},
    {id: 'badge' as const, label: 'Badges', count: badgeCount},
    {id: 'identity' as const, label: "Pièces d'identité", count: identityCount},
  ]

  return (
    <section className='dashboard-renewals-card'>
      <div className='dashboard-renewals-header'>
        <div>
          <h3>Documents à renouveler</h3>
          <p>
            {filteredRows.length} document{filteredRows.length > 1 ? 's' : ''} affiché
            {filteredRows.length > 1 ? 's' : ''} · trié par {urgentFirst ? 'échéance' : 'nom'}
          </p>
        </div>
        <div className='dashboard-renewals-tools'>
          <label className='dashboard-renewals-search'>
            <i className='bi bi-search' />
            <input
              aria-label='Rechercher un document à renouveler'
              placeholder='Rechercher un nom, lieu...'
              type='search'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <button
            className='dashboard-renewals-sort'
            type='button'
            onClick={() => setUrgentFirst((value) => !value)}
          >
            <i className='bi bi-arrow-down-up' />
            {urgentFirst ? 'Plus urgent' : 'Nom A-Z'}
          </button>
        </div>
      </div>

      <div className='dashboard-renewals-tabs' role='tablist' aria-label='Filtrer les documents'>
        {tabs.map((tab) => (
          <button
            className={activeTab === tab.id ? 'active' : ''}
            key={tab.id}
            type='button'
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span>{tab.count}</span>
          </button>
        ))}
      </div>

      <div className='dashboard-renewals-table'>
        <div className='dashboard-renewals-table-head'>
          <span>Personne</span>
          <span>Document</span>
          <span>Fonction</span>
          <span>Échéance</span>
        </div>
        <div className='dashboard-renewals-table-body'>
          {filteredRows.map((row) => {
            const tone = getRenewalTone(row.daysLeft)
            return (
              <button
                className='dashboard-renewals-row'
                key={row.id}
                type='button'
                onClick={() =>
                  navigate(
                    row.collaboratorId
                      ? `/admin/collaborator/${row.collaboratorId}/${
                          row.documentType === 'badge' ? 'badge' : 'overview'
                        }`
                      : '/admin/collaborators'
                  )
                }
              >
                <span className='dashboard-renewals-person'>
                  <span className={`dashboard-renewals-avatar dashboard-renewals-avatar-${tone}`}>
                    {row.initials}
                  </span>
                  <span>
                    <strong>{row.fullName}</strong>
                    <small>{row.personMeta}</small>
                  </span>
                </span>
                <span className='dashboard-renewals-document'>
                  <i
                    className={`bi ${
                      row.documentType === 'badge' ? 'bi-person-badge' : 'bi-card-text'
                    }`}
                  />
                  <span>
                    <strong>{row.documentLabel}</strong>
                    <small>{row.reference}</small>
                  </span>
                </span>
                <span className='dashboard-renewals-function'>
                  <i className='bi bi-briefcase' />
                  <span>
                    <strong>{row.role}</strong>
                    <small>Fonction</small>
                  </span>
                </span>
                <span className='dashboard-renewals-deadline'>
                  <b className={`dashboard-renewals-pill dashboard-renewals-pill-${tone}`}>
                    {getRenewalDelayLabel(row.daysLeft)}
                  </b>
                  <small>le {row.dueDate.format('D MMM YYYY')}</small>
                </span>
              </button>
            )
          })}
          {filteredRows.length === 0 && (
            <div className='dashboard-renewals-empty'>
              Aucun document ne correspond à ce filtre.
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

const DashboardPage: FC = () => {
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const month = useMemo(() => moment().locale('fr').startOf('month'), [])
  const previousMonth = useMemo(() => moment(month).subtract(1, 'month'), [month])
  const [data, setData] = useState<DashboardData | undefined>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (currentUser?.role === 'collaborator') {
      navigate('/collaborator/planning/overview')
      return
    }
    if (currentUser?.role === 'client') {
      navigate('/client/planning/overview')
      return
    }

    ;(async () => {
      setLoading(true)
      const from = moment(month).subtract(5, 'month').startOf('month').format('YYYY-MM-DD')
      const to = moment(month).endOf('month').format('YYYY-MM-DD')
      const period = month.format('YYYY-MM-DD')
      const previousPeriod = previousMonth.format('YYYY-MM-DD')

      const criticalRequests = [
        getVoicesArray(),
        getQuotations(''),
        getPlannings(`period=${period}`),
        getPlannings(`period=${previousPeriod}`),
        getPlanningsAvailable(period),
        getContracts({filter_period: period}),
        getCAdata(from, to),
        getCAClientdata(
          month.format('YYYY-MM-DD'),
          moment(month).endOf('month').format('YYYY-MM-DD')
        ),
        getCollaboratorsArray(),
        getClientArrau(100000),
        getBrandsArray(100000),
        getLocationsArray(100000),
      ]
      const [results, badgesResponse] = await Promise.all([
        Promise.allSettled(criticalRequests),
        getBadges('').catch(() => ({data: []})),
      ])

      const read = <T,>(index: number, fallback: T): T =>
        results[index].status === 'fulfilled'
          ? (results[index] as PromiseFulfilledResult<T>).value ?? fallback
          : fallback

      const contractResponse = read<any>(5, {data: [], count: 0})
      const planningResponse = read<any>(2, {data: []})
      const previousPlanningResponse = read<any>(3, {data: []})
      const collaborators = await enrichCollaboratorsWithRenewalDetails(
        read<CollaboratorUser[]>(8, [])
      )
      setData({
        invoices: read<Voice[]>(0, []),
        quotations: read<any>(1, {data: []})?.data ?? [],
        plannings: planningResponse?.data ?? [],
        previousPlannings: previousPlanningResponse?.data ?? [],
        collaborators,
        badges: badgesResponse?.data ?? [],
        clients: read<ClientUser[]>(9, []),
        brands: read<ClientBrand[]>(10, []),
        locations: read<Location[]>(11, []),
        planningState: read<any>(4, undefined),
        contracts: contractResponse?.data ?? [],
        contractsCount: contractResponse?.count ?? contractResponse?.data?.length ?? 0,
        turnover: read<RevenuePoint[]>(6, []),
        turnoverByClient: read<RevenuePoint[]>(7, []),
      })

      setLoading(false)
    })()
  }, [currentUser?.role, month, navigate, previousMonth])

  const metrics = useMemo(() => {
    const invoices = data?.invoices || []
    const quotations = data?.quotations || []
    const plannings = data?.plannings || []
    const previousPlannings = data?.previousPlannings || []
    const contracts = data?.contracts || []

    const monthInvoices = invoices.filter((invoice) => isInMonth(invoice.created_at, month))
    const previousInvoices = invoices.filter((invoice) =>
      isInMonth(invoice.created_at, previousMonth)
    )
    const monthInvoiceTotal = monthInvoices.reduce((sum, invoice) => sum + amountOf(invoice), 0)
    const previousInvoiceTotal = previousInvoices.reduce(
      (sum, invoice) => sum + amountOf(invoice),
      0
    )
    const invoiceDelta =
      previousInvoiceTotal > 0
        ? Math.round(((monthInvoiceTotal - previousInvoiceTotal) / previousInvoiceTotal) * 1000) /
          10
        : undefined

    const toInvoiceQuotations = quotations.filter(isToInvoice)
    const toInvoiceAmount = toInvoiceQuotations.reduce(
      (sum, quotation) => sum + safeNumber(quotation.amount),
      0
    )

    const totalShifts = plannings.length
    const planningHours = Math.round(
      plannings.reduce((sum, planning) => sum + getPlanningHours(planning), 0)
    )
    const previousMonthShifts = previousPlannings.length
    const shiftDelta =
      previousMonthShifts > 0
        ? Math.round(((totalShifts - previousMonthShifts) / previousMonthShifts) * 1000) / 10
        : undefined
    const conflicts = plannings.filter((planning) => planning.intersections?.length).length
    const unassigned = plannings.filter((planning) => !hasAssignedCollaborator(planning)).length

    const lastContractDate = contracts
      .map((contract) => contract?.created?.date || contract?.date)
      .filter(Boolean)
      .sort()
      .pop()

    return {
      monthInvoices,
      monthInvoiceTotal,
      invoiceDelta,
      toInvoiceQuotations,
      toInvoiceAmount,
      totalShifts,
      planningHours,
      shiftDelta,
      conflicts,
      unassigned,
      lastContractDate,
    }
  }, [data, month, previousMonth])

  const chartData = useMemo(() => {
    const months = Array.from({length: 6}, (_, index) =>
      moment(month)
        .subtract(5 - index, 'month')
        .startOf('month')
    )
    const actualByKey = new Map(
      (data?.turnover || []).map((item) => [normalizeKey(item.id), safeNumber(item.amount)])
    )

    return months.map((item) => ({
      label: item.format('MMM'),
      actual: actualByKey.get(item.format('YYYY-MM')) || 0,
    }))
  }, [data?.turnover, month])

  const topClients = useMemo<TopClient[]>(() => {
    const source = data?.turnoverByClient || []
    const total = source.reduce((sum, item) => sum + safeNumber(item.amount), 0)
    return source
      .map((item) => ({
        name: item.id || 'Client non renseigne',
        amount: safeNumber(item.amount),
        percent: total > 0 ? Math.round((safeNumber(item.amount) / total) * 100) : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)
  }, [data?.turnoverByClient])

  const workItems: WorkItem[] = [
    {
      label: 'Devis à facturer',
      description: `${formatMoney(metrics.toInvoiceAmount)} à convertir`,
      value: metrics.toInvoiceQuotations.length,
      tone: 'warning',
      target: '/admin/quotation/overview',
    },
    {
      label: 'Factures sans statut',
      description: `${formatMoney(
        metrics.monthInvoices
          .filter((invoice) => !invoice.status)
          .reduce((sum, invoice) => sum + amountOf(invoice), 0)
      )} · statut non renseigné`,
      value: metrics.monthInvoices.filter((invoice) => !invoice.status).length,
      tone: 'danger',
      target: '/admin/invoice',
    },
    {
      label: 'Conflits de planning',
      description: 'Créneaux qui se chevauchent',
      value: metrics.conflicts,
      tone: 'danger',
      target: '/admin/planning/overview',
    },
    {
      label: 'Shifts sans promoteur',
      description: 'Postes à pourvoir',
      value: metrics.unassigned,
      tone: 'warning',
      target: '/admin/planning/overview',
    },
  ]

  const actualTotal = chartData.reduce((sum, item) => sum + item.actual, 0)
  const handleExport = () => {
    const rows = [
      ['Metric', 'Valeur'],
      ['CA facture', Math.round(metrics.monthInvoiceTotal)],
      ['Factures du mois', metrics.monthInvoices.length],
      ['Factures sans statut', metrics.monthInvoices.filter((invoice) => !invoice.status).length],
      ['Devis a facturer', metrics.toInvoiceQuotations.length],
      ['Montant devis a facturer', Math.round(metrics.toInvoiceAmount)],
      ['Shifts planifies', metrics.totalShifts],
      ['Heures planifiees', metrics.planningHours],
      ['Conflits planning', metrics.conflicts],
      ['Shifts sans promoteur', metrics.unassigned],
      ['Contrats generes', data?.contractsCount || 0],
    ]
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n')
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `dashboard-kamellia-${month.format('YYYY-MM')}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className='dashboard-heading'>
        <div>
          <p>
            Vue d'ensemble de l'activité <span className='dashboard-live-dot' /> Dernière mise à
            jour : aujourd'hui à {moment().format('HH:mm')}
          </p>
        </div>
        <div className='dashboard-heading-actions'>
          <div className='dashboard-period-button' aria-label='Période affichée'>
            <i className='bi bi-calendar3' />
            {month.format('MMMM YYYY')}
          </div>
          <button type='button' className='dashboard-export-button' onClick={handleExport}>
            <i className='bi bi-download' />
            Exporter
          </button>
        </div>
      </div>

      {loading ? (
        <div className='dashboard-card dashboard-loading'>Chargement du tableau de bord...</div>
      ) : (
        <>
          <div className='dashboard-metrics-grid'>
            <MetricCard
              accent='primary'
              icon='bi-receipt'
              label='CA facturé'
              value={formatMoney(metrics.monthInvoiceTotal)}
              suffix='HT'
              target='/admin/invoice'
              detail={
                <>
                  {metrics.invoiceDelta !== undefined ? (
                    <>
                      <span
                        className={
                          metrics.invoiceDelta >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'
                        }
                      >
                        <i
                          className={`bi ${
                            metrics.invoiceDelta >= 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'
                          }`}
                        />{' '}
                        {metrics.invoiceDelta}%
                      </span>{' '}
                      vs mois préc. ·{' '}
                    </>
                  ) : (
                    <>Mois précédent indisponible · </>
                  )}
                  {metrics.monthInvoices.length} factures
                </>
              }
            />
            <MetricCard
              accent='warning'
              icon='bi-clipboard-check'
              label='Devis à facturer'
              value={`${metrics.toInvoiceQuotations.length}`}
              suffix='devis'
              target='/admin/quotation/overview'
              detail={
                <>
                  <span className='text-warning fw-bold'>À facturer</span> ·{' '}
                  {formatMoney(metrics.toInvoiceAmount)}
                </>
              }
            />
            <MetricCard
              accent='purple'
              icon='bi-calendar4-week'
              label='Shifts planifiés'
              value={`${metrics.totalShifts}`}
              suffix='shifts'
              target='/admin/planning/overview'
              detail={
                <>
                  {metrics.shiftDelta !== undefined ? (
                    <>
                      <span
                        className={
                          metrics.shiftDelta >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'
                        }
                      >
                        <i
                          className={`bi ${
                            metrics.shiftDelta >= 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'
                          }`}
                        />{' '}
                        {metrics.shiftDelta}%
                      </span>{' '}
                      vs mois préc. ·{' '}
                    </>
                  ) : (
                    <>Comparaison indisponible · </>
                  )}
                  {metrics.planningHours} h
                </>
              }
            />
            <MetricCard
              accent='success'
              icon='bi-file-earmark-text'
              label='Contrats générés'
              value={`${data?.contractsCount || 0}`}
              suffix='contrats'
              target='/admin/contract'
              detail={
                <>
                  Dernière génération :{' '}
                  {metrics.lastContractDate
                    ? moment(metrics.lastContractDate).format('D MMM')
                    : '-'}
                </>
              }
            />
          </div>
          <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
            <div className='col-xl-8'>
              <RevenueChart
                actualTotal={actualTotal}
                data={chartData}
                invoiceCount={metrics.monthInvoices.length}
              />
            </div>
            <div className='col-xl-4'>
              <WorkQueue items={workItems} />
            </div>
          </div>

          <div className='row g-5 g-xl-8'>
            <div className='col-xl-7'>
              <PlanningHealth
                brands={data?.brands || []}
                badges={data?.badges || []}
                clients={data?.clients || []}
                collaborators={data?.collaborators || []}
                locations={data?.locations || []}
                plannings={data?.plannings || []}
                previousPlannings={data?.previousPlannings || []}
              />
            </div>
            <div className='col-xl-5'>
              <TopClients clients={topClients} />
            </div>
          </div>

          <DocumentRenewalsTable
            badges={data?.badges || []}
            collaborators={data?.collaborators || []}
            plannings={data?.plannings || []}
          />
        </>
      )}
    </>
  )
}

const DashboardWrapper: FC = () => {
  useEffect(() => {
    document.body.classList.add('dashboard-page-active')

    return () => {
      document.body.classList.remove('dashboard-page-active')
    }
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={[]}>Accueil</PageTitle>
      <div className='dashboard-surface'>
        <DashboardPage />
      </div>
    </>
  )
}

export {DashboardWrapper}
